import React, { useEffect, useState } from 'react';
import {
  Stack,
  Text,
  Group,
  Badge,
  Card,
  ActionIcon,
  Menu,
  Box,
  Tooltip,
  NumberInput,
  LoadingOverlay,
  Title,
  Skeleton,
  Button,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconClock,
  IconUsers,
  IconDotsVertical,
  IconRefresh,
  IconCheck,
  IconHeart,
  IconInfoCircle,
  IconTrash,
  IconMessageCircle,
  IconChartBar,
  IconPrinter,
  IconChefHat,
  IconView360Arrow,
  IconThumbUp,
  IconThumbDown,
  IconThumbUpFilled,
  IconThumbDownFilled,
  } from '@tabler/icons-react';
import { MealPlan, MealPlanAssignment, MealType, Recipe, getAssignmentMealType, SubstitutionGenerationProgress } from '../../types/mealPlan';
import { isMealPlanPast } from '../../utils/mealPlanUtils';
import { getReferenceAnchor, assignmentSubstitutionKey } from '../../utils/mealPlanHelpers';
import { formatTime } from '../../utils/formatTime';
import { displayFractions } from '../../utils/formatQuantity';
import { formatIngredientName, formatIngredientQuantity } from '../../utils/formatIngredient';
import { getRecipeBadges, splitBadgesForDisplay } from '../../utils/recipeBadges';
import { printRecipe } from '../../utils/printRecipe';
import { MoreRestrictionsMenu } from '../UI/MoreRestrictionsMenu';
import { HouseholdSegment } from '../../utils/householdSegment';
import { RecipeCarousel } from './RecipeCarousel';
import { SubstitutionProgress } from './SubstitutionProgress';
import { usePostHog } from '@posthog/react';
import { AnalyticsEvent } from '../../utils/analytics';
import { AuthButton } from '../AuthButton';
import { RecipeImage } from '../UI/RecipeImage';
import { CookbookRibbon, TopPickRibbon, TrendingRibbon, recipeMark } from '../UI/TopPickRibbon';
import { recipeSourceChipLabel, recipeSourceTooltip, sourceBadgeStyle, sourceTooltipProps } from '../Recipe/RecipeProvenanceBadges';
import { CostPerServingPopover } from '../UI/CostPerServingPopover';
import { CuisineBadge } from '../UI/CuisineBadge';

interface RecipeCardProps {
  assignment: MealPlanAssignment;
  isOwner: boolean;
  mealPlan: MealPlan;
  loadingActivation: boolean;
  menuOpen: Record<string, boolean>;
  openTooltip: string | null;
  servingsInputMode: string | null;
  tempServings: number;
  savingDays: Set<string>;
  substitutingDays: Set<string>;
  // Live AI-substitution progress by plannedDay; when this card's day has an
  // entry, the loading overlay narrates the swap instead of a bare spinner.
  substitutionProgressByKey?: Record<string, SubstitutionGenerationProgress>;
  swappingDays: Set<string>;
  ratingDays: Set<string>;
  handleAssignmentClick: (assignment: MealPlanAssignment, overrideRecipe?: Recipe) => void;
  handleToggleSaveRecipe: (recipeId: string) => void;
  handleTooltipClick: (day: string, e: React.MouseEvent) => void;
  handleSubstituteWithInstructions: (day: string) => void;
  handleServingsInputMode: (day: string, servings: number) => void;
  handleSubmitServings: (day: string) => void;
  handleAssignmentFeedback: (assignment: MealPlanAssignment) => void;
  handleRateUp: (assignment: MealPlanAssignment) => void;
  handleRateDown: (assignment: MealPlanAssignment) => void;
  handleUndoRating: (assignment: MealPlanAssignment) => void;
  handleDeleteAssignment: (assignment: MealPlanAssignment) => void;
  handleSwapAlternative: (assignment: MealPlanAssignment, recipe: Recipe, swapSequence: number, targetRecipeIdRef: React.RefObject<string>) => Promise<void>;
  setMenuOpen: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setServingsInputMode: React.Dispatch<React.SetStateAction<string | null>>;
  setTempServings: React.Dispatch<React.SetStateAction<number>>;
  setSelectedRecipeForNutrition: React.Dispatch<React.SetStateAction<Recipe | null>>;
  setNutritionDrawerOpened: React.Dispatch<React.SetStateAction<boolean>>;
  isFirstCard?: boolean;
  userDietaryRestrictions?: string[] | null;
  // Household framing for the viewer (householdSegment util); gates
  // family-only tags like "kid-friendly". Omit when unknown.
  userHouseholdSegment?: HouseholdSegment;
  // Demo mode for landing page - hides interactive elements (save, menu, reasoning) and disables API calls
  demoMode?: boolean;
  demoDays?: string[]; // Day labels for each recipe in demo carousel (index 0 = main, 1+ = alternatives)
  onDemoCtaClick?: () => void; // Called when "View full recipe" is clicked in demo mode
  radius?: 'sm' | 'md' | 'lg' | 'xl'; // Card border radius
  // Ref for carousel swap flush coordination with activation
  pendingSwapCallbacksRef?: React.RefObject<Map<string, () => Promise<void>>>;
  // Optimistic preview: maps cook day plannedDate → currently-visible carousel recipe
  // Keyed by `${plannedDate}#${mealType}` - a breakfast anchor and a dinner
  // anchor share dates, so a bare-date key would cross slot previews
  cookDayPreviews?: Record<string, Recipe>;
  onCookDayPreview?: (
    plannedDate: string,
    mealType: MealType,
    recipe: Recipe | null
  ) => void;
  // Optional drag handle rendered on the card's front face (inside the 3D flip
  // container) so it rotates with the card during the flip animation.
  dragHandle?: React.ReactNode;
  // Initial flipped recipe id - lets a freshly-mounted card (e.g. the drag overlay
  // clone) start on its back face to match the card being dragged.
  initialFlippedRecipeId?: string | null;
  // Notified whenever the flipped recipe changes, so a parent can mirror the state.
  onFlipChange?: (flippedRecipeId: string | null) => void;
}

// Whether the batch was actually cooked before its anchor's day. Stale
// cookedOnDate values (drags can leave them at or past the eating day)
// don't count.
const wasCookedAhead = (anchor: MealPlanAssignment): boolean =>
  !!anchor.cookedOnDate && anchor.cookedOnDate < anchor.plannedDate;

// The day a batch was actually cooked: cookedOnDate when it validly precedes
// the eating day (drags can leave stale values), the eating day otherwise.
// "Made ahead on Monday" for a batch prepped Sunday was a lie.
const effectiveCookDayName = (anchor: MealPlanAssignment): string => {
  if (anchor.cookedOnDate && anchor.cookedOnDate < anchor.plannedDate) {
    const [y, m, d] = anchor.cookedOnDate.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long' });
  }
  return anchor.plannedDay;
};

export const RecipeCard: React.FC<RecipeCardProps> = ({
  assignment,
  isOwner,
  mealPlan,
  loadingActivation,
  menuOpen,
  openTooltip,
  servingsInputMode,
  tempServings,
  savingDays,
  substitutingDays,
  substitutionProgressByKey,
  swappingDays,
  ratingDays,
  handleAssignmentClick,
  handleToggleSaveRecipe,
  handleTooltipClick,
  handleSubstituteWithInstructions,
  handleServingsInputMode,
  handleSubmitServings,
  handleAssignmentFeedback,
  handleRateUp,
  handleRateDown,
  handleUndoRating,
  handleDeleteAssignment,
  handleSwapAlternative,
  setMenuOpen,
  setServingsInputMode,
  setTempServings,
  setSelectedRecipeForNutrition,
  setNutritionDrawerOpened,
  isFirstCard = false,
  userDietaryRestrictions,
  userHouseholdSegment,
  demoMode = false,
  demoDays,
  onDemoCtaClick,
  radius,
  pendingSwapCallbacksRef,
  cookDayPreviews,
  onCookDayPreview,
  dragHandle,
  initialFlippedRecipeId = null,
  onFlipChange,
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isNarrowMobile = useMediaQuery('(max-width: 480px)'); // For back side layout
  // Below this the card is too tight for two ingredient columns to read well
  const fitsTwoIngredientColumns = useMediaQuery('(min-width: 360px)');
  const posthog = usePostHog();
  const isTabletOrSmaller = useMediaQuery('(max-width: 1280px)');
  const [showNutritionOverlay, setShowNutritionOverlay] = useState(false);
  const [flippedRecipeId, setFlippedRecipeId] = useState<string | null>(initialFlippedRecipeId);

  // Mirror flip changes to a parent (used so the drag overlay can match a flipped card)
  useEffect(() => {
    onFlipChange?.(flippedRecipeId);
  }, [flippedRecipeId, onFlipChange]);

  const lastFlippedRecipeRef = React.useRef<Recipe | null>(null);

  // Check if this is a prep day and find its anchor (the assignment whose
  // recipe this day serves). Slot-aware via getReferenceAnchor: batch
  // rotation days anchor to the same slot's cook, leftover lunches and
  // dinner prep days to the dinner.
  const isPrepDay = assignment.source === 'assignment_reference';
  const cardMealType = getAssignmentMealType(assignment);
  const cookDayAssignment = isPrepDay
    ? getReferenceAnchor(assignment, mealPlan)
    : undefined;
  const anchorMealType = cookDayAssignment
    ? getAssignmentMealType(cookDayAssignment)
    : 'dinner';

  // Check if this is a cook day (has other days prepping from it)
  const isCookDay = assignment.isCookDay || false;

  // References served by THIS card, slot-aware: a breakfast block's
  // references must not show up on the same date's dinner card (mirrors the
  // backend's referencesFeeding rule)
  const prepRefs = isCookDay
    ? mealPlan.assignments.filter(ref => {
        if (ref.source !== 'assignment_reference' || ref.preparedOnDate !== assignment.plannedDate) {
          return false;
        }
        const refSlot = getAssignmentMealType(ref);
        if (refSlot === cardMealType) return true;
        if (cardMealType !== 'dinner') return false;
        const sameSlotAnchor = mealPlan.assignments.find(
          a =>
            a.plannedDate === ref.preparedOnDate &&
            getAssignmentMealType(a) === refSlot &&
            a.source !== 'assignment_reference' &&
            !!a.scaledRecipe
        );
        return !sameSlotAnchor;
      })
    : [];
  const dinnerPrepDays = prepRefs
    .filter(a => getAssignmentMealType(a) === 'dinner')
    .map(a => a.plannedDay);
  const leftoverLunchDays = prepRefs
    .filter(a => getAssignmentMealType(a) === 'lunch')
    .map(a => a.plannedDay);
  // Labeled union, used where the kinds share copy (delete gate + hint)
  const prepDays = prepRefs.map(a =>
    getAssignmentMealType(a) === 'lunch' && cardMealType === 'dinner'
      ? `${a.plannedDay} lunch`
      : a.plannedDay
  );
  // "Makes 4 meals: Thursday and Friday dinners, plus Friday and Saturday
  // lunches" — one summary sentence instead of stitched-together fragments.
  // Non-dinner cooks are single-slot batches: "One batch makes 3 breakfasts:
  // Monday, Tuesday, and Wednesday."
  const joinDays = (days: string[]) =>
    days.length <= 1
      ? days[0] ?? ''
      : days.length === 2
        ? `${days[0]} and ${days[1]}`
        : `${days.slice(0, -1).join(', ')}, and ${days[days.length - 1]}`;
  const dinnerDays = [assignment.plannedDay, ...dinnerPrepDays];
  const mealCount = dinnerDays.length + leftoverLunchDays.length;
  const cookedAheadDate =
    assignment.cookedOnDate && assignment.cookedOnDate < assignment.plannedDate
      ? assignment.cookedOnDate
      : undefined;
  // The dimmed image means "this food was cooked on an earlier day", which is
  // as true of a cooked-ahead anchor as of its reference days: nobody cooks on
  // the anchor's day either. The crisp image's home is the Cook plan tab's
  // labor-day row.
  const eatsMadeAheadFood = isPrepDay || !!cookedAheadDate;
  const cookedAheadPrefix = cookedAheadDate
    ? `Cooked on ${(() => {
        const [y, m, d] = cookedAheadDate.split('-').map(Number);
        return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long' });
      })()}. `
    : '';
  const cookDayTooltip =
    cookedAheadPrefix +
    (cardMealType === 'dinner'
      ? `Makes ${mealCount} meals: ${[
          `${joinDays(dinnerDays)} dinner${dinnerDays.length !== 1 ? 's' : ''}`,
          leftoverLunchDays.length > 0
            ? `${joinDays(leftoverLunchDays)} lunch${leftoverLunchDays.length !== 1 ? 'es' : ''}`
            : null,
        ]
          .filter(Boolean)
          .join(', plus ')}.`
      : `One batch makes ${prepRefs.length + 1} ${cardMealType}${prepRefs.length + 1 !== 1 ? 's' : ''}: ${joinDays([assignment.plannedDay, ...prepRefs.map(a => a.plannedDay)])}.`);

  // Resolve the recipe - use cook day's recipe for prep days
  // Check optimistic preview first (updates immediately on carousel swipe).
  // Previews are keyed by date and driven by the DINNER carousel, so only
  // dinner-anchored references may consume them.
  // Reference days preview their anchor's carousel swipes instantly for
  // every slot (the anchorMealType key keeps a leftover lunch reading the
  // dinner anchor's preview and a made-ahead breakfast reading its own)
  const previewRecipe = isPrepDay && assignment.preparedOnDate
    ? cookDayPreviews?.[`${assignment.preparedOnDate}#${anchorMealType}`]
    : undefined;
  const recipeAssignment = isPrepDay && cookDayAssignment?.scaledRecipe
    ? cookDayAssignment
    : assignment;
  const resolvedRecipe = previewRecipe ?? recipeAssignment.scaledRecipe;
  // Trending is snapshotted on the assignment, not the recipe (see
  // Alternative.isTrending), so put it back on the selected recipe for
  // recipeMark. Without it a swapped-in trending recipe fell back to Top pick
  // once the server response replaced the carousel's copy. Memoized so the
  // carousel's selectedRecipe keeps a stable identity.
  const selectedIsTrending = !previewRecipe && recipeAssignment.isTrending === true;
  const recipe = React.useMemo(
    () => resolvedRecipe && selectedIsTrending && !resolvedRecipe.isTrending
      ? { ...resolvedRecipe, isTrending: true }
      : resolvedRecipe,
    [resolvedRecipe, selectedIsTrending]
  );

  // If we can't resolve the recipe, return null (shouldn't happen in practice)
  if (!recipe) {
    return null;
  }

  // Check if this prep day's cook day is being substituted
  const cookDayIsSubstituting = isPrepDay && cookDayAssignment && (
    cookDayAssignment.substitutionStatus?.status === 'pending' ||
    cookDayAssignment.substitutionStatus?.status === 'generating' ||
    substitutingDays.has(assignmentSubstitutionKey(cookDayAssignment))
  );

  // Rotation blocks swap as one unit (the backend applies a breakfast/lunch
  // substitution to every day holding the same recipe), so every day of the
  // block shows the in-flight state — same principle as prep days spinning
  // while their cook day is substituted. Block-mates are identified exactly
  // the way the backend does: same slot, same recipe, not a reference.
  const blockMateIsSubstituting =
    cardMealType !== 'dinner' &&
    !isPrepDay &&
    !!assignment.scaledRecipe &&
    mealPlan.assignments.some(a =>
      a.id !== assignment.id &&
      getAssignmentMealType(a) === cardMealType &&
      a.source !== 'assignment_reference' &&
      a.scaledRecipe?.id === assignment.scaledRecipe!.id &&
      (a.substitutionStatus?.status === 'pending' ||
        a.substitutionStatus?.status === 'generating' ||
        substitutingDays.has(assignmentSubstitutionKey(a)))
    );

  const isLoading = assignment.substitutionStatus?.status === 'pending' ||
                    assignment.substitutionStatus?.status === 'generating' ||
                    substitutingDays.has(assignmentSubstitutionKey(assignment)) ||
                    cookDayIsSubstituting ||
                    blockMateIsSubstituting;

  // Prep days show their cook day's swap, so progress resolves through it.
  // Only the AI path ever streams progress; when there is none (cookbook
  // pick, servings change, or the task hasn't started), the overlay keeps
  // its plain spinner rather than narrating stages that aren't happening.
  const substitutionSourceAssignment = isPrepDay ? cookDayAssignment : assignment;
  const substitutionProgress = isLoading && substitutionSourceAssignment
    ? substitutionProgressByKey?.[assignmentSubstitutionKey(substitutionSourceAssignment)]
    : undefined;

  // Carousel logic - only show on cook days/regular days, not prep days
  const alternatives = assignment.alternatives || [];
  const hasAlternatives = alternatives.length > 0;
  const isDraft = mealPlan.status === 'draft';
  // Past plans are read-only: keep view-only menu items (nutrition, feedback,
  // ratings) but hide everything that would modify the plan.
  const planIsPast = isMealPlanPast(mealPlan);
  const showCarousel = hasAlternatives && isDraft && !planIsPast && !isPrepDay && (isOwner || demoMode);

  // Handle swapping an alternative to become the selected recipe
  const handleSwap = async (alternativeRecipe: Recipe, swapSequence: number, targetRecipeIdRef: React.RefObject<string>) => {
    await handleSwapAlternative(assignment, alternativeRecipe, swapSequence, targetRecipeIdRef);
  };

  // Helper to render the back side of the flipped card
  const renderBackSide = (displayRecipe: Recipe) => {
    // Show more content on desktop/tablet (side-by-side layout), less on narrow mobile (stacked)
    // Two columns fit roughly twice the lines in the same vertical space
    const twoColumnIngredients = isNarrowMobile && fitsTwoIngredientColumns;
    const maxIngredients = isNarrowMobile ? (twoColumnIngredients ? 14 : 7) : 16;
    const maxInstructions = isNarrowMobile ? 4 : 8;
    const ingredients = displayRecipe.ingredients || [];
    const instructions = displayRecipe.instructions || [];
    const moreIngredients = ingredients.length > maxIngredients ? ingredients.length - maxIngredients : 0;
    const moreInstructions = instructions.length > maxInstructions ? instructions.length - maxInstructions : 0;

    const ingredientsSection = (
      <>
        <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={4}>
          Ingredients
        </Text>
        <Box
          style={
            twoColumnIngredients
              // CSS columns balance themselves, so no line-height math that
              // could spill into a third column
              ? { columnCount: 2, columnGap: 12 }
              : { display: 'flex', flexDirection: 'column', rowGap: 1 }
          }
        >
          {ingredients.slice(0, maxIngredients).map((ingredient, index) => {
            // Optional ingredients the household's diet rules out. The card is
            // too compact for the badge the detail view shows, so a
            // strikethrough carries it: this is the line you leave out, and
            // it is why the grocery list does not have it.
            const excluded = Boolean(ingredient.conflictsWith?.length);
            return (
              <Text
                key={index}
                size="xs"
                lineClamp={1}
                style={twoColumnIngredients ? { breakInside: 'avoid' } : undefined}
                td={excluded ? 'line-through' : undefined}
                c={excluded ? 'dimmed' : undefined}
                title={
                  excluded
                    ? `Not ${ingredient.conflictsWith!.join(', ').toLowerCase()}. Left out of your grocery list.`
                    : undefined
                }
              >
                <Text component="span" fw={600} size="xs">
                  {formatIngredientQuantity(ingredient)}
                </Text>
                {' '}
                {formatIngredientName(ingredient)}
              </Text>
            );
          })}
          {moreIngredients > 0 && (
            <Text size="xs" c="dimmed" fs="italic">
              +{moreIngredients} more
            </Text>
          )}
        </Box>
      </>
    );

    const instructionsSection = (
      <>
        <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={4}>
          Instructions
        </Text>
        <Stack gap={4}>
          {instructions.slice(0, maxInstructions).map((instruction, index) => (
            <Group key={index} gap={6} align="flex-start" wrap="nowrap">
              <Text size="xs" fw={700} c="blue" style={{ flexShrink: 0, minWidth: 16 }}>
                {index + 1}.
              </Text>
              <Text size="xs" lineClamp={2} style={{ flex: 1 }}>
                {displayFractions(instruction.text)}
              </Text>
            </Group>
          ))}
          {moreInstructions > 0 && (
            <Text size="xs" c="dimmed" fs="italic">
              +{moreInstructions} more step{moreInstructions > 1 ? 's' : ''}
            </Text>
          )}
        </Stack>
      </>
    );

    return (
      <>
        {/* Header with recipe name */}
        <Text fw={600} size="sm" lineClamp={1} mb="xs" ta="center">
          {displayRecipe.name}
        </Text>

        {/* Content area - side by side on tablet+, stacked on narrow mobile */}
        <Box style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
          {isNarrowMobile ? (
            // Narrow mobile: stacked layout
            <Stack gap="sm">
              <Box>{ingredientsSection}</Box>
              <Box>{instructionsSection}</Box>
            </Stack>
          ) : (
            // Tablet+: side-by-side layout - ingredients narrower, instructions wider
            <Box style={{ display: 'flex', gap: 16 }}>
              <Box style={{ flex: '0 0 35%', minWidth: 0 }}>
                {ingredientsSection}
              </Box>
              <Box style={{ flex: '1 1 65%', minWidth: 0 }}>
                {instructionsSection}
              </Box>
            </Box>
          )}
        </Box>

        {/* View full recipe + flip back. Tapping anywhere flips the card
            back, but that is not discoverable, so pair an explicit control
            with the primary action. */}
        <Group
          gap="xs"
          wrap="nowrap"
          onClick={(e) => e.stopPropagation()}
          style={{ pointerEvents: 'auto', position: 'relative', zIndex: 10, marginTop: 'var(--mantine-spacing-xs)' }}
        >
          <Box style={{ flex: 1, minWidth: 0 }}>
            {demoMode ? (
              <AuthButton size="sm" fullWidth variant="light">
                View full recipe
              </AuthButton>
            ) : (
              <Button
                variant="light"
                size="sm"
                fullWidth
                style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleAssignmentClick(assignment, displayRecipe);
                }}
              >
                View full recipe
              </Button>
            )}
          </Box>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            aria-label="Back to recipe card"
            style={{ flexShrink: 0, pointerEvents: 'auto' }}
            onClick={(e) => {
              e.stopPropagation();
              setFlippedRecipeId(null);
            }}
          >
            <IconView360Arrow size={20} />
          </ActionIcon>
        </Group>
      </>
    );
  };

  // Render function for recipe content (used by carousel)
  // source: the recipe's source for optimistic badge display (from carousel's sourceMap)
  // isCarousel: true when rendering inside a carousel (for tour highlighting)
  // Dietary restriction + tag badge row, shared by the mobile (2 badges) and
  // tablet+ (3 badges) breakpoint groups.
  // Space the bottom-right flip/nutrition buttons occupy, reserved by the rows
  // that reach the card's bottom edge so their badges never slide underneath.
  // Each lg ActionIcon is 34px, plus the 4px gap and the 11px right offset.
  const cornerActionCount =
    (!demoMode && !isPrepDay && !isLoading ? 1 : 0) +
    (isTabletOrSmaller && recipe.nutrition ? 1 : 0);
  const cornerActionsWidth = cornerActionCount
    ? cornerActionCount * 34 + (cornerActionCount - 1) * 4 + 11
    : undefined;

  const renderBadgeRow = (displayRecipe: Recipe, maxVisible: number, skeletonWidths: number[]) => {
    const restrictionCount = displayRecipe.dietaryRestrictions?.length || 0;
    const totalBadges = restrictionCount + (displayRecipe.tags?.length || 0);

    // Show skeleton badges while user preferences are loading
    if (userDietaryRestrictions === null && totalBadges > 0) {
      const visibleCount = Math.min(totalBadges, maxVisible);
      // Only overflowing restrictions get a "+N more", so approximate with those
      const hasMore = restrictionCount > maxVisible;

      return (
        <>
          {Array.from({ length: visibleCount }).map((_, i) => (
            <Skeleton key={`skeleton-${i}`} height={18} width={skeletonWidths[i % skeletonWidths.length] || 80} radius="xl" />
          ))}
          {hasMore && (
            <Skeleton height={18} width={60} radius="xl" />
          )}
        </>
      );
    }

    // Don't render anything if still loading with no badges
    if (userDietaryRestrictions === null || userDietaryRestrictions === undefined) {
      return null;
    }

    const allBadges = getRecipeBadges(
      displayRecipe,
      { dietaryRestrictions: userDietaryRestrictions, householdSegment: userHouseholdSegment },
      // In demo mode, ensure user-selected restrictions are shown even if filtered as redundant
      { includeUserSelectedRedundant: demoMode }
    );

    // In demo mode every user-matching restriction stays visible, so the
    // viewer always sees their own selections on the card
    const userMatchingCount = allBadges.filter(
      (b) =>
        b.type === 'dietary' &&
        userDietaryRestrictions.some(
          (r) => r.toLowerCase() === b.text.toLowerCase()
        )
    ).length;
    const maxBadges = demoMode ? Math.max(userMatchingCount, maxVisible) : maxVisible;
    const { visible, hiddenRestrictions } = splitBadgesForDisplay(allBadges, maxBadges);

    return (
      <>
        {visible.map((badge) => (
          <Badge key={`${badge.type}-${badge.text}`} size="sm" variant="outline">
            {badge.text}
          </Badge>
        ))}
        {!demoMode && <MoreRestrictionsMenu restrictions={hiddenRestrictions} />}
      </>
    );
  };

  const renderRecipeContent = (displayRecipe: Recipe, isSelected: boolean, reasoning?: string, source?: 'user_recipe' | 'ai_generated' | 'assignment_reference', isCarousel?: boolean) => {
    const isFlipped = displayRecipe.id === flippedRecipeId;

    // In demo mode, show different days for each recipe in the carousel
    const dayLabel = (() => {
      if (demoMode && demoDays) {
        if (displayRecipe.id === recipe.id) return demoDays[0] || assignment.plannedDay;
        const altIndex = alternatives.findIndex(a => a.recipe.id === displayRecipe.id);
        return demoDays[altIndex + 1] || assignment.plannedDay;
      }
      return assignment.plannedDay;
    })();

    // Handle image click - open recipe details (flip in demo mode only)
    const handleImageClick = () => {
      if (isLoading) return;
      if (demoMode) {
        // In demo mode, flip the card since we can't navigate to details
        setFlippedRecipeId(isFlipped ? null : displayRecipe.id);
      } else {
        // In normal mode, always open recipe details
        handleAssignmentClick(assignment, displayRecipe);
      }
    };

    const frontContent = (
    <Stack gap="md">
      {/* Recipe Image */}
      <Box
          data-tour={isCarousel ? "recipe-carousel" : undefined}
          data-carousel-drag-area="true"
          style={{
            position: 'relative',
            borderRadius: 8,
            overflow: 'hidden',
            cursor: isLoading ? 'default' : 'pointer',
            pointerEvents: 'auto',
          }}
          onClick={handleImageClick}
          onMouseEnter={() => !isTabletOrSmaller && !isLoading && displayRecipe.nutrition && setShowNutritionOverlay(true)}
          onMouseLeave={() => !isTabletOrSmaller && setShowNutritionOverlay(false)}
        >
          <RecipeImage
            src={displayRecipe.imageUrl}
            alt={displayRecipe.name}
            height={200}
            draggable={false}
            crossfade={isPrepDay}
            style={{
              userSelect: 'none',
              ...(eatsMadeAheadFood ? {
                filter: 'saturate(0.4) brightness(0.9)',
                opacity: 0.5
              } : {})
            } as React.CSSProperties}
          />
          {/* Overlay for made-ahead days */}
          {eatsMadeAheadFood && displayRecipe.imageUrl && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.12) 0%, rgba(245, 158, 11, 0.15) 100%)',
              pointerEvents: 'none'
            }} />
          )}
          {/* The chef-hat badge and its makes-N tooltip mark a day you
              actually cook. A cooked-ahead anchor (Sunday labor, Monday
              first serving) is effectively another made-ahead day, so it
              falls through to the plain day badge in yellow like its
              reference days; the Cook plan tab tells the batch's story. */}
          {isCookDay && prepDays.length > 0 && !cookedAheadDate ? (
            <Tooltip
              label={cookDayTooltip}
              position="bottom"
              withArrow
              multiline
              w={240}
            >
              <Badge
                variant="filled"
                color="orange"
                style={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  zIndex: 1,
                  cursor: isLoading ? 'default' : 'pointer'
                }}
                leftSection={<IconChefHat size={14} />}
              >
                {dayLabel}
              </Badge>
            </Tooltip>
          ) : (
            <Badge
              variant="filled"
              color={isPrepDay || cookedAheadDate ? 'yellow' : 'orange'}
              style={{
                position: 'absolute',
                top: 8,
                left: 8,
                zIndex: 1,
                cursor: isLoading ? 'default' : 'pointer'
              }}
              leftSection={isCookDay && !cookedAheadDate ? <IconChefHat size={14} /> : undefined}
            >
              {dayLabel}
            </Badge>
          )}

          {/* Heart icon for AI-generated recipes or user recipes no longer saved - hide on prep days, non-selected slides, and demo mode */}
          {/* Use source from carousel for optimistic UI, fall back to assignment.source for non-carousel */}
          {isSelected && !isPrepDay && isOwner && !demoMode && ((source ?? assignment.source) === 'ai_generated' || !displayRecipe.isSaved) && (
            <Box
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
                cursor: (savingDays.has(assignmentSubstitutionKey(assignment)) || isLoading) ? 'default' : 'pointer',
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
                opacity: (savingDays.has(assignmentSubstitutionKey(assignment)) || isLoading) ? 0.6 : 1
              }}
              onClick={(e) => {
                e.stopPropagation();
                !(savingDays.has(assignmentSubstitutionKey(assignment)) || isLoading) && handleToggleSaveRecipe(displayRecipe.id);
              }}
              data-tour={isFirstCard ? 'recipe-save' : undefined}
              data-no-carousel-drag="true"
            >
              {displayRecipe.isSaved === true ? (
                <div
                  style={{
                    width: 24,
                    height: 24,
                    background: 'linear-gradient(45deg, rgb(253, 126, 20) 0%, rgb(250, 82, 82) 100%)',
                    mask: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'currentColor\'><path d=\'M6.979 3.074a6 6 0 0 1 4.988 1.425l.037 .033l.034 -.03a6 6 0 0 1 4.733 -1.44l.246 .036a6 6 0 0 1 3.364 10.008l-.18 .185l-.048 .041l-7.45 7.379a1 1 0 0 1 -1.313 .082l-.094 -.082l-7.493 -7.422a6 6 0 0 1 3.176 -10.215z\'/></svg>") center/contain no-repeat',
                    WebkitMask: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'currentColor\'><path d=\'M6.979 3.074a6 6 0 0 1 4.988 1.425l.037 .033l.034 -.03a6 6 0 0 1 4.733 -1.44l.246 .036a6 6 0 0 1 3.364 10.008l-.18 .185l-.048 .041l-7.45 7.379a1 1 0 0 1 -1.313 .082l-.094 -.082l-7.493 -7.422a6 6 0 0 1 3.176 -10.215z\'/></svg>") center/contain no-repeat'
                  }}
                />
              ) : (
                <IconHeart
                  size={24}
                  color="white"
                  stroke={2}
                  fill="rgba(0, 0, 0, 0.2)"
                />
              )}
            </Box>
          )}

          {/* Nutrition overlay - hover-reveal on desktop, tap-reveal on mobile in demo mode */}
          {displayRecipe.nutrition && (!isTabletOrSmaller || demoMode) && (
                <Box
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(to top, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.5) 40%, rgba(0, 0, 0, 0.25) 70%, transparent 100%)',
                    padding: '40px 12px 12px 12px',
                    opacity: showNutritionOverlay ? 1 : 0,
                    transition: 'opacity 0.2s ease',
                    pointerEvents: showNutritionOverlay ? 'auto' : 'none',
                    zIndex: 2,
                  }}
                >
                  <Box
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isTabletOrSmaller) {
                        // Mobile: overlay is tap-revealed, so tapping it dismisses
                        setShowNutritionOverlay(false);
                      } else if (demoMode) {
                        setFlippedRecipeId(flippedRecipeId === displayRecipe.id ? null : displayRecipe.id);
                      } else {
                        setSelectedRecipeForNutrition(displayRecipe);
                        setTimeout(() => setNutritionDrawerOpened(true), 0);
                      }
                    }}
                  >
                    <Text size="xs" fw={600} c="white" mb={4}>
                      Per serving
                    </Text>
                    <Stack gap={2}>
                      <Text size="sm" c="white" style={{ lineHeight: 1.3 }}>
                        {displayRecipe.nutrition.perServing.calories} cal • {displayRecipe.nutrition.perServing.fat}g fat • {displayRecipe.nutrition.perServing.sodium}mg sodium
                      </Text>
                      <Text size="sm" c="white" style={{ lineHeight: 1.3 }}>
                        {displayRecipe.nutrition.perServing.carbohydrates}g carbs • {displayRecipe.nutrition.perServing.fiber}g fiber • {displayRecipe.nutrition.perServing.protein}g protein
                      </Text>
                    </Stack>
                  </Box>
                </Box>
          )}

          {/* One ribbon, by precedence: the user's own cookbook beats what
              other households are cooking, which beats our own endorsement.
              See recipeMark. source alone isn't enough for cookbook: any
              hand-picked recipe is user_recipe, including ARCHIVE picks —
              isSaved is what separates cookbook from archive. */}
          {!isPrepDay &&
            (() => {
              switch (
                recipeMark({
                  isCookbook:
                    (source ?? assignment.source) === 'user_recipe' &&
                    displayRecipe.isSaved === true,
                  isTrending: displayRecipe.isTrending,
                  isCurated: displayRecipe.isCurated,
                })
              ) {
                case 'cookbook':
                  return <CookbookRibbon />;
                case 'trending':
                  return <TrendingRibbon />;
                case 'topPick':
                  return <TopPickRibbon />;
                default:
                  return null;
              }
            })()}

                </Box>

      {/* Day and Title */}
      <Box style={{ position: 'relative', overflow: 'visible', display: 'flow-root' }}>
        {/* Floated spacer reserving the top-right corner for the icon group so
            the recipe name wraps beside the icons on its first line, then flows
            full width beneath them instead of staying crammed in a narrow
            column. The real icons render absolutely on top of this spacer. */}
        {!demoMode && (
          <Box
            aria-hidden
            style={{ float: 'right', width: 84, height: 32, pointerEvents: 'none' }}
          />
        )}
        <Title
          order={4}
          style={{
            cursor: isLoading ? 'default' : 'pointer',
            pointerEvents: 'auto',
            marginBottom: displayRecipe.description ? 'var(--mantine-spacing-xs)' : 0,
          }}
          onClick={() => {
            if (isLoading) return;
            if (demoMode) {
              setFlippedRecipeId(flippedRecipeId === displayRecipe.id ? null : displayRecipe.id);
            } else {
              handleAssignmentClick(assignment, displayRecipe);
            }
          }}
        >
          {displayRecipe.name}
        </Title>
        {displayRecipe.description && (
          <Text size="sm" c="dimmed" lineClamp={demoMode ? 3 : 2} style={{ clear: 'right' }}>
            {displayRecipe.description}
          </Text>
        )}

        <Group gap="xs" style={{ position: 'absolute', top: -4, right: 0 }}>
          {/* Reasoning info icon - hide for prep days, non-selected slides, and demo mode */}
          {isSelected && !isPrepDay && !demoMode && reasoning && (
            <Tooltip
              label={reasoning}
              position="top"
              withArrow
              multiline
              w={280}
              opened={openTooltip === assignment.plannedDay}
              styles={{
                tooltip: {
                  fontSize: '0.75rem',
                  lineHeight: 1.4
                }
              }}
            >
              <ActionIcon
                variant="subtle"
                size="lg"
                c="dimmed"
                onClick={(e) => !isLoading && handleTooltipClick(assignment.plannedDay, e)}
                style={{ pointerEvents: 'auto', cursor: isLoading ? 'default' : 'pointer' }}
              >
                <IconInfoCircle size={20} />
              </ActionIcon>
            </Tooltip>
          )}


          {isSelected && isOwner && !demoMode && !(planIsPast && isPrepDay && !displayRecipe.nutrition) && (
            <Menu
              position="bottom-end"
              width={isMobile ? 280 : 240}
              opened={menuOpen[assignment.plannedDay] || false}
              onChange={(opened) => {
                if (isLoading) return;
                if (opened) {
                  posthog?.capture(AnalyticsEvent.RecipeMenuOpened, {
                    planned_day: assignment.plannedDay,
                    meal_type: cardMealType,
                  });
                }
                setMenuOpen(prev => ({ ...prev, [assignment.plannedDay]: opened }));
                if (!opened) setServingsInputMode(null);
              }}
            >
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  data-tour={isFirstCard ? 'recipe-menu' : undefined}
                  style={{ pointerEvents: 'auto', cursor: isLoading ? 'default' : 'pointer' }}
                >
                  <IconDotsVertical size={20} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
              {/* Swapping a reference day detaches it from its block: the day
                  becomes its own fresh meal and the cook's coverage shrinks
                  derivedly (the anchor still cooks what was planned). Only
                  past plans hide the option. */}
              {!planIsPast && (
                <Menu.Item
                  leftSection={<IconRefresh size={isMobile ? 18 : 14} stroke={1.5} />}
                  onClick={() => {
                    handleSubstituteWithInstructions(assignment.plannedDay);
                  }}
                  disabled={
                    loadingActivation ||
                    mealPlan.groceryListStatus?.status === 'pending' ||
                    mealPlan.groceryListStatus?.status === 'generating' ||
                    assignment.substitutionStatus?.status === 'pending' ||
                    assignment.substitutionStatus?.status === 'generating' ||
                    substitutingDays.has(assignmentSubstitutionKey(assignment))
                  }
                  style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                >
                  {assignment.substitutionStatus?.status === 'pending' ||
                   assignment.substitutionStatus?.status === 'generating' ||
                   substitutingDays.has(assignmentSubstitutionKey(assignment))
                    ? 'Finding new recipe...'
                    : 'Swap this meal...'
                  }
                </Menu.Item>
              )}


              {/* Hide servings options for prep days and past plans */}
              {!isPrepDay && !planIsPast && servingsInputMode === assignment.plannedDay ? (
                <Menu.Item
                  onClick={(e) => e.stopPropagation()}
                  closeMenuOnClick={false}
                  style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                >
                  <Group gap="xs" align="center">
                    <IconUsers size={isMobile ? 18 : 14} stroke={1.5} />
                    <Text size={isMobile ? "md" : "sm"}>Servings</Text>
                    <NumberInput
                      value={tempServings}
                      onChange={(value) => setTempServings(typeof value === 'number' ? value : 1)}
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e?.stopPropagation?.()}
                      onKeyDown={(e) => {
                        e?.stopPropagation?.();
                        if (e.key === 'Enter') {
                          handleSubmitServings(assignment.plannedDay);
                        }
                      }}
                      min={isCookDay ? displayRecipe.servings : 1}
                      max={20}
                      size={isMobile ? "md" : "xs"}
                      w={isMobile ? 70 : 60}
                      style={{ pointerEvents: 'auto' }}
                      {...(isMobile ? { inputMode: 'none' as const } : {})}
                    />
                    <ActionIcon
                      size={isMobile ? "lg" : "md"}
                      color="green"
                      onClick={() => handleSubmitServings(assignment.plannedDay)}
                      disabled={tempServings === displayRecipe.servings}
                      style={{ pointerEvents: 'auto' }}
                    >
                      <IconCheck size={16} />
                    </ActionIcon>
                  </Group>
                </Menu.Item>
              ) : !isPrepDay && !planIsPast ? (
                <Menu.Item
                  leftSection={<IconUsers size={isMobile ? 18 : 14} stroke={1.5} />}
                  onClick={() => {
                    handleServingsInputMode(assignment.plannedDay, displayRecipe.servings);
                  }}
                  closeMenuOnClick={false}
                  disabled={
                    loadingActivation ||
                    mealPlan.groceryListStatus?.status === 'pending' ||
                    mealPlan.groceryListStatus?.status === 'generating' ||
                    assignment.substitutionStatus?.status === 'pending' ||
                    assignment.substitutionStatus?.status === 'generating' ||
                    substitutingDays.has(assignmentSubstitutionKey(assignment))
                  }
                  style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                >
                  Change servings...
                </Menu.Item>
              ) : null}

              {!planIsPast && (
                <Menu.Item
                  leftSection={<IconTrash size={isMobile ? 18 : 14} stroke={1.5} />}
                  color="red"
                  onClick={() => {
                    handleDeleteAssignment(assignment);
                  }}
                  disabled={
                    loadingActivation ||
                    mealPlan.groceryListStatus?.status === 'pending' ||
                    mealPlan.groceryListStatus?.status === 'generating' ||
                    (isCookDay && prepDays.length > 0)
                  }
                  style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                >
                  Remove day
                  {/* The cook-day gate is the only disable reason that isn't
                      obvious or transient: explain the dependency and how to
                      unblock it */}
                  {isCookDay && prepDays.length > 0 && (
                    <Text size="xs" c="dimmed">
                      Feeds {prepDays.join(' and ')}. Remove {prepDays.length > 1 ? 'those meals' : 'that meal'} first.
                    </Text>
                  )}
                </Menu.Item>
              )}

              {!isPrepDay && !planIsPast && <Menu.Divider />}

              {displayRecipe.nutrition && (
                <Menu.Item
                  leftSection={<IconChartBar size={isMobile ? 18 : 14} stroke={1.5} />}
                  onClick={() => {
                    setSelectedRecipeForNutrition(displayRecipe);
                    setTimeout(() => setNutritionDrawerOpened(true), 0);
                  }}
                  style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                >
                  Nutrition facts
                </Menu.Item>
              )}

              {!isPrepDay && (
                <Menu.Item
                  leftSection={<IconMessageCircle size={isMobile ? 18 : 14} stroke={1.5} />}
                  onClick={() => handleAssignmentFeedback(assignment)}
                  style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                >
                  Send feedback
                </Menu.Item>
              )}

              <Menu.Item
                leftSection={<IconPrinter size={isMobile ? 18 : 14} stroke={1.5} />}
                onClick={() => printRecipe(displayRecipe)}
                style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
              >
                Print recipe
              </Menu.Item>

              {!isPrepDay && displayRecipe.origin !== 'user_recipe' && (
                <Group gap="xs" px={isMobile ? 16 : 12} pt={isMobile ? 8 : 6} pb={isMobile ? 8 : 6} justify="center">
                  <ActionIcon
                    variant={displayRecipe.rating === 'up' ? 'light' : 'default'}
                    color={displayRecipe.rating === 'up' ? 'green' : undefined}
                    size={isMobile ? 'lg' : 'md'}
                    loading={ratingDays.has(assignmentSubstitutionKey(assignment)) && displayRecipe.rating === 'up'}
                    disabled={displayRecipe.rating === 'down'}
                    onClick={() => displayRecipe.rating === 'up' ? handleUndoRating(assignment) : handleRateUp(assignment)}
                  >
                    {displayRecipe.rating === 'up' ? <IconThumbUpFilled size={16} /> : <IconThumbUp size={16} stroke={1.5} />}
                  </ActionIcon>
                  <ActionIcon
                    variant={displayRecipe.rating === 'down' ? 'light' : 'default'}
                    color={displayRecipe.rating === 'down' ? 'red' : undefined}
                    size={isMobile ? 'lg' : 'md'}
                    loading={ratingDays.has(assignmentSubstitutionKey(assignment)) && displayRecipe.rating === 'down'}
                    disabled={displayRecipe.rating === 'up'}
                    onClick={() => displayRecipe.rating === 'down' ? handleUndoRating(assignment) : handleRateDown(assignment)}
                  >
                    {displayRecipe.rating === 'down' ? <IconThumbDownFilled size={16} /> : <IconThumbDown size={16} stroke={1.5} />}
                  </ActionIcon>
                </Group>
              )}

            </Menu.Dropdown>
            </Menu>
          )}
        </Group>
      </Box>

      {/* Recipe Details */}
      <Group gap="xs">
        {!isPrepDay && (
          <Group gap="xs">
            <IconClock size={16} />
            <Text size="sm">{formatTime(displayRecipe.totalTimeMinutes)}</Text>
          </Group>
        )}
        <Group gap="xs">
          <IconUsers size={16} />
          <Text size="sm">{isPrepDay ? assignment.servings : displayRecipe.servings} {(isPrepDay ? assignment.servings : displayRecipe.servings) === 1 ? 'serving' : 'servings'}</Text>
        </Group>
        {!isPrepDay && displayRecipe.difficulty && (
          <Badge
            size="sm"
            variant="light"
            color={displayRecipe.difficulty === 'easy' ? 'green' : displayRecipe.difficulty === 'medium' ? 'yellow' : 'red'}
          >
            {displayRecipe.difficulty}
          </Badge>
        )}
        {!isPrepDay && displayRecipe.costEstimate && (
          <CostPerServingPopover
            costEstimate={displayRecipe.costEstimate}
            dineOut={displayRecipe.estimatedDineOutPricePerServing}
          />
        )}
      </Group>

      {/* Cuisine, Dietary Restrictions and Tags */}
      {/* The flip/nutrition buttons float over the card's bottom-right corner,
          so the rows that reach the bottom reserve room for them; otherwise
          badges and "+N more" slide underneath. */}
      <Group gap="xs" wrap="wrap" mb={demoMode ? "sm" : undefined} pr={cornerActionsWidth}>
        {displayRecipe.cuisine && (
          <CuisineBadge cuisine={displayRecipe.cuisine} />
        )}
        {/* Mobile: Show 2 badges max */}
        <Group gap="xs" hiddenFrom="sm" style={{ display: 'contents' }}>
          {renderBadgeRow(displayRecipe, 2, [80, 70])}
        </Group>

        {/* Tablet+: Show 3 badges max */}
        <Group gap="xs" visibleFrom="sm" style={{ display: 'contents' }}>
          {renderBadgeRow(displayRecipe, 3, [80, 90, 70])}
        </Group>
      </Group>

      {/* Source - hide in demo mode */}
      {!demoMode && (
        <Group justify="space-between" mt="auto" pr={cornerActionsWidth}>
          <Group gap="xs">
            {/* Relationship (cookbook), popularity (trending) and endorsement
                (top pick) share one image ribbon; dot badges carry authorship
                when notable (My recipe / USDA — house recipes stay unmarked)
                plus operational info. Reference days carry the unified
                "Leftovers from X's dinner" / "Made ahead" badge instead of a
                meal prep dot. */}
            {!isPrepDay && displayRecipe.origin === 'user_recipe' && (
              <Badge size="xs" variant="dot" color="teal">
                My recipe
              </Badge>
            )}
            {!isPrepDay && displayRecipe.source && (
              <Tooltip label={recipeSourceTooltip(displayRecipe.source)} {...sourceTooltipProps}>
                <Badge size="xs" variant="dot" color="indigo" style={sourceBadgeStyle}>
                  {recipeSourceChipLabel(displayRecipe.source)}
                </Badge>
              </Tooltip>
            )}
            {/* One badge, one pattern for every day that eats from an earlier
                cook: dinner prep days and leftover lunches are the same
                concept, and "leftovers" is the word users actually use. The
                exception is a dinner served from a cooked-ahead batch: that's
                another serving of the batch, not leftovers of a dinner cooked
                on the anchor's day, so it wears the anchor's own made-ahead
                badge. A lunch keeps the leftovers copy either way, since its
                anchor day names the dinner it came from, which stays true. */}
            {isPrepDay && cookDayAssignment && (
              <Badge size="xs" variant="light" color="yellow">
                {anchorMealType === 'dinner' &&
                !(cardMealType === 'dinner' && wasCookedAhead(cookDayAssignment))
                  ? `Leftovers from ${cookDayAssignment.plannedDay}'s dinner`
                  : `Made ahead on ${effectiveCookDayName(cookDayAssignment)}`}
              </Badge>
            )}
            {/* A cooked-ahead anchor eats made-ahead food same as its
                reference days (it just holds the batch's recipe), so it
                wears the same badge naming the real labor day */}
            {!isPrepDay && cookedAheadDate && (
              <Badge size="xs" variant="light" color="yellow">
                Made ahead on {effectiveCookDayName(assignment)}
              </Badge>
            )}
          </Group>
        </Group>
      )}
    </Stack>
    );

    return frontContent;
  };

  // Determine if this card can flip (not prep day, not loading)
  const canFlipCard = !isPrepDay && !isLoading;
  // Card is flipped if any recipe (selected or alternative) is flipped
  const isCardFlipped = canFlipCard && flippedRecipeId !== null;

  // Find the flipped recipe (could be main recipe or an alternative)
  // Cache it so the back card doesn't flash wrong content during flip-back animation
  const currentFlippedRecipe = flippedRecipeId === recipe.id
    ? recipe
    : alternatives.find(alt => alt.recipe.id === flippedRecipeId)?.recipe;

  if (currentFlippedRecipe) {
    lastFlippedRecipeRef.current = currentFlippedRecipe;
  }

  const flippedRecipe = currentFlippedRecipe || lastFlippedRecipeRef.current || recipe;

  const frontCard = (
    <Card
      withBorder
      h="100%"
      radius={radius}
      style={{
        opacity: isLoading ? 0.7 : 1,
        position: 'relative',
        backfaceVisibility: canFlipCard ? 'hidden' : undefined,
        WebkitBackfaceVisibility: canFlipCard ? 'hidden' : undefined,
        transform: canFlipCard ? 'rotateY(0deg)' : undefined,
        WebkitTransform: canFlipCard ? 'rotateY(0deg)' : undefined,
        // Safari fallback: explicitly hide when flipped since backface-visibility can be unreliable
        // Delay hiding until card rotates past 90deg (0.4s into 0.8s animation)
        visibility: isCardFlipped ? 'hidden' : 'visible',
        transition: canFlipCard ? (isCardFlipped ? 'visibility 0s 0.4s' : 'visibility 0s 0s') : undefined,
        WebkitTransition: canFlipCard ? (isCardFlipped ? 'visibility 0s 0.4s' : 'visibility 0s 0s') : undefined,
        pointerEvents: isCardFlipped ? 'none' : undefined,
      }}
      data-tour={isFirstCard ? 'recipe-card-first' : undefined}
    >
      {isLoading && (
        <LoadingOverlay
          visible
          // Above everything in the card (highest in-card zIndex is 10) but
          // below Mantine drawers and modals (200): the default 400 painted
          // over open drawers whenever the card creates no stacking context
          zIndex={50}
          // The progress plate carries text, so the card content behind it is
          // softened with a blur the bare spinner never needed.
          overlayProps={substitutionProgress ? { blur: 2 } : undefined}
          loaderProps={
            substitutionProgress
              ? {
                  children: (
                    <SubstitutionProgress
                      progress={substitutionProgress}
                      household={mealPlan.profileSnapshot}
                      mealType={getAssignmentMealType(assignment)}
                    />
                  ),
                }
              : { size: 'sm' }
          }
        />
      )}
      <Stack gap="md" h="100%">
        {showCarousel ? (
          <Box h="100%">
            <RecipeCarousel
              selectedRecipe={recipe}
              selectedReasoning={assignment.reasoning}
              selectedSource={assignment.source === 'custom_meal' ? 'user_recipe' : assignment.source}
              alternatives={alternatives}
              assignment={assignment}
              isOwner={isOwner}
              isDraft={isDraft}
              disabled={isLoading}
              hidden={isCardFlipped}
              onSwap={handleSwap}
              renderRecipeContent={renderRecipeContent}
              alternativesOrder={assignment.alternativesOrder}
              demoMode={demoMode}
              pendingSwapCallbacksRef={pendingSwapCallbacksRef}
              onPreviewRecipe={isCookDay && onCookDayPreview
                ? (previewRecipe) => onCookDayPreview(assignment.plannedDate, cardMealType, previewRecipe)
                : undefined}
            />
          </Box>
        ) : (
          renderRecipeContent(
            recipe,
            true,
            assignment.reasoning,
            // Custom meals never render this card; fallback satisfies the recipe-source type
            assignment.source === 'custom_meal' ? 'user_recipe' : assignment.source
          )
        )}

        {/* Bottom-right action buttons (flip + nutrition on mobile) */}
        {((!demoMode && canFlipCard) || (isTabletOrSmaller && recipe.nutrition)) && (
          <Group
            gap={4}
            style={{
              position: 'absolute',
              bottom: 7,
              right: 11,
              zIndex: 3, // Above carousel indicators (zIndex: 2)
            }}
          >
            {!demoMode && canFlipCard && (
              <ActionIcon
                variant="subtle"
                color="gray"
                size="lg"
                style={{ pointerEvents: 'auto' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setFlippedRecipeId(flippedRecipeId === recipe.id ? null : recipe.id);
                }}
              >
                <IconView360Arrow size={20} />
              </ActionIcon>
            )}
            {isTabletOrSmaller && recipe.nutrition && (
              <ActionIcon
                variant="subtle"
                color="gray"
                size="lg"
                style={{ pointerEvents: 'auto' }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (demoMode) {
                    // Demo has no nutrition drawer; toggle the inline overlay instead
                    setShowNutritionOverlay((prev) => !prev);
                  } else {
                    setSelectedRecipeForNutrition(recipe);
                    setTimeout(() => setNutritionDrawerOpened(true), 0);
                  }
                }}
              >
                <IconChartBar size={20} />
              </ActionIcon>
            )}
          </Group>
        )}
      </Stack>
    </Card>
  );

  // If card can't flip, just return the front card (with the drag handle anchored
  // to a relative wrapper so it isn't clipped by the Card's overflow: hidden).
  if (!canFlipCard) {
    return dragHandle ? (
      <Box style={{ position: 'relative', height: '100%' }}>
        {frontCard}
        {dragHandle}
      </Box>
    ) : frontCard;
  }

  // Wrap in 3D flip container for non-carousel cards
  return (
    <Box
      style={{
        perspective: '1000px',
        WebkitPerspective: '1000px',
        height: '100%',
      }}
    >
      <Box
        style={{
          position: 'relative',
          height: '100%',
          transformStyle: 'preserve-3d',
          WebkitTransformStyle: 'preserve-3d',
          transition: 'transform 0.8s ease',
          WebkitTransition: 'transform 0.8s ease',
          transform: isCardFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* Front side */}
        {frontCard}

        {/* Back side */}
        <Card
          withBorder
          h="100%"
          radius={radius}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            WebkitTransform: 'rotateY(180deg)',
            // Safari fallback: explicitly hide when not flipped since backface-visibility can be unreliable
            // Delay hiding until card rotates past 90deg (0.4s into 0.8s animation)
            visibility: isCardFlipped ? 'visible' : 'hidden',
            transition: isCardFlipped ? 'visibility 0s 0s' : 'visibility 0s 0.4s',
            WebkitTransition: isCardFlipped ? 'visibility 0s 0s' : 'visibility 0s 0.4s',
            cursor: 'pointer',
            pointerEvents: isCardFlipped ? 'auto' : 'none',
          }}
          onClick={() => setFlippedRecipeId(null)}
        >
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            {renderBackSide(flippedRecipe)}
          </Box>
        </Card>

        {/* Drag handle - coplanar with the front face so it rotates with the card
            during the flip, but stays visible (its mirrored back face is identical). */}
        {dragHandle}
      </Box>
    </Box>
  );
};
